import {
  REPORT_CANVAS_MAX_COMPONENTS,
  type CanvasGenerationComponent,
  type CanvasGenerationDataset,
} from '@erp/rpc';
import {
  REPORT_CANVAS_COLUMNS,
  ReportCanvasGridLayoutSchema,
} from '@erp/shared-types';
import { componentSize, type ComponentRegistry } from './registry';
import type { GridLayout, GridRect, ReportComponentConfig } from './types';
import type { ReportDataset } from './ReportDataContext';
import { deserializeLayout } from './serialization';
import { hydrateLayout } from './hydrate';
import { newNodeId } from './layout';
import { bottomSpot, clampRect, rowsToGrid } from './grid';
import { validateSpecObject } from './vega/VegaChartWidget';

// ── Whole-canvas generation pipeline ──────────────────────────────────────────
// The model returns a serialized layout ({ items: [{ x, y, w, h, component }] }
// on the Report grid) as loose JSON — x/y/w/h rather than rows, so it can lay
// a tall chart beside two short ones; everything positional it gets wrong
// (overlaps, holes, out-of-grid sizes) is fixed deterministically below and by
// compaction, so positions never cost a repair round. A response in the rows
// shape layouts were saved in before the grid still converts. This module owns turning that into a trustworthy GridLayout:
//
//   normalize (deterministic auto-fixes — never waste the repair round on
//   things we can fix locally) → schema parse → semantic issue collection
//   (unknown types, bad column refs, invalid Custom chart specs) → ONE repair
//   round with the issue list → drop still-broken components → deserialize +
//   hydrate.
//
// The repair round and the drop step exist because LLM output is best-effort:
// a layout that is 90% right should land as a draft the user can touch up,
// not fail wholesale.

type LooseRecord = Record<string, unknown>;

function isRecord(value: unknown): value is LooseRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ── Catalogs sent to the model ────────────────────────────────────────────────

/**
 * The surface's registry as a machine-readable catalog: type, label, the AI
 * `description` hint, and the make() defaults minus identity fields (the
 * model must not be taught to emit ids/v — they're injected client-side).
 */
export function buildComponentCatalog(
  registry: ComponentRegistry,
): CanvasGenerationComponent[] {
  return Object.values(registry).map((def) => {
    const defaults: LooseRecord = { ...def.make('catalog-sample') };
    delete defaults.id;
    delete defaults.v;
    delete defaults.type;
    const { w, h, minW, minH } = componentSize(registry, def.type);
    return {
      type: def.type,
      label: def.label,
      description: def.description,
      defaultConfig: defaults,
      size: { w, h, minW, minH },
    };
  });
}

export function buildDatasetCatalog(
  datasets: ReportDataset[],
): CanvasGenerationDataset[] {
  return datasets.map((d) => ({
    key: d.key,
    label: d.label,
    columns: d.columns,
    sampleRows: d.rows.slice(0, 3),
  }));
}

// ── Normalization (deterministic auto-fixes) ──────────────────────────────────

type LooseItem = GridRect & { component: LooseRecord };
type LooseLayout = { items: LooseItem[] };
type Placement = { component: LooseRecord } & Partial<GridRect>;

function gridNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value)
    : undefined;
}

/** An `items` entry: `{ x, y, w, h, component }`, or a component with its place on it. */
function placementOf(entry: LooseRecord): Placement | null {
  const { x, y, w, h, component, ...rest } = entry;
  const config = isRecord(component) ? component : 'type' in rest ? rest : null;
  if (!config) return null;
  return {
    component: config,
    x: gridNumber(x),
    y: gridNumber(y),
    w: gridNumber(w),
    h: gridNumber(h),
  };
}

/** The response's components with whatever places it gave them. */
function placements(
  raw: LooseRecord,
  registry: ComponentRegistry,
): Placement[] | null {
  if (Array.isArray(raw.items))
    return raw.items.flatMap((entry: unknown) => {
      const placement = isRecord(entry) ? placementOf(entry) : null;
      return placement ? [placement] : [];
    });
  if (!Array.isArray(raw.data)) return null;
  const rows = raw.data.flatMap((row: unknown) => {
    const cells = Array.isArray(row) ? row.filter(isRecord) : [];
    return cells.length
      ? [
          cells.map((cell): LooseRecord & { span?: number } => ({
            ...cell,
            span: gridNumber(cell.span),
          })),
        ]
      : [];
  });
  return rowsToGrid(
    rows,
    (cell) => componentSize(registry, String(cell.type)).h,
  );
}

/** Injects a missing or repeated id, strips `v`, drops an unknown datasetKey. */
function normalizedComponent(
  config: LooseRecord,
  seenIds: Set<string>,
  datasetKeys: ReadonlySet<string>,
): LooseRecord {
  const component: LooseRecord = { ...config };
  if (
    typeof component.id !== 'string' ||
    component.id.length === 0 ||
    seenIds.has(component.id)
  ) {
    component.id = newNodeId();
  }
  seenIds.add(String(component.id));
  delete component.v;
  if (
    component.datasetKey !== undefined &&
    (typeof component.datasetKey !== 'string' ||
      !datasetKeys.has(component.datasetKey))
  ) {
    delete component.datasetKey;
  }
  return component;
}

/**
 * Repairs everything a program can repair without a model round-trip:
 * injects missing/duplicate ids, strips `v` (hydration stamps it), deletes
 * unknown datasetKeys (component falls back to the surface default), drops
 * non-object entries, sizes a component the model left unsized at its type's
 * default (and no smaller than its minimum, within the grid), places one it
 * left unplaced at the bottom, and truncates to the layout cap. Overlaps are
 * left for compaction to resolve on load. Returns null when the response
 * isn't a layout at all.
 */
export function normalizeGeneratedLayout(
  raw: unknown,
  datasetKeys: ReadonlySet<string>,
  registry: ComponentRegistry,
): LooseLayout | null {
  if (!isRecord(raw)) return null;
  const placed = placements(raw, registry);
  if (!placed) return null;

  const seenIds = new Set<string>();
  const items: LooseItem[] = [];
  for (const placement of placed.slice(0, REPORT_CANVAS_MAX_COMPONENTS)) {
    const component = normalizedComponent(
      placement.component,
      seenIds,
      datasetKeys,
    );
    const size = componentSize(registry, String(component.type));
    const w = Math.min(
      REPORT_CANVAS_COLUMNS,
      Math.max(size.minW, placement.w ?? size.w),
    );
    const h = Math.max(size.minH, placement.h ?? size.h);
    const spot =
      placement.x !== undefined && placement.y !== undefined
        ? { x: placement.x, y: placement.y }
        : bottomSpot(items, { w, h });
    items.push({ ...clampRect({ ...spot, w, h }), component });
  }

  if (items.length === 0) return null;
  return { items };
}

// ── Semantic issue collection ─────────────────────────────────────────────────

/**
 * Why a builtin chart's column refs don't bind to its dataset: one naming no
 * column, or one left unset (the chart would read "Not set up yet" instead
 * of drawing). Null when they all bind, or there is no dataset to check.
 */
function columnRefIssue(
  type: string,
  refFields: readonly string[] | undefined,
  component: LooseRecord,
  dataset: ReportDataset | null,
): string | null {
  if (!refFields || !dataset) return null;
  const isSet = (value: unknown) =>
    value !== undefined && value !== null && value !== '';
  const badRefs = refFields.filter((field) => {
    const value = component[field];
    return (
      isSet(value) && !dataset.columns.some((column) => column.key === value)
    );
  });
  if (badRefs.length > 0)
    return `${type}: ${badRefs
      .map((field) => `${field}="${String(component[field])}"`)
      .join(', ')} — not a column of dataset "${dataset.key}"`;
  const unset = refFields.filter((field) => !isSet(component[field]));
  if (unset.length > 0)
    return `${type}: ${unset.join(', ')} not set — each must name a column of dataset "${dataset.key}"`;
  return null;
}

/** Why a Custom chart can't be drawn from its dataset; null when it can. */
async function customChartIssue(
  component: LooseRecord,
  dataset: ReportDataset | null,
): Promise<string | null> {
  const spec = component.spec;
  if (!isRecord(spec)) return 'vega-chart is missing its "spec" config';
  const grammar = component.grammar === 'vega' ? 'vega' : 'vega-lite';
  const specError = await validateSpecObject(spec, grammar, dataset?.rows);
  return specError
    ? `vega-chart ${grammar} spec is invalid: ${specError}`
    : null;
}

/**
 * Issues a program can detect but not fix: unknown component types, builtin
 * chart column refs that don't exist in the bound dataset, Custom chart specs
 * that fail validation or don't render against the dataset. Keyed by component id so broken components can be dropped
 * after the repair round.
 */
export async function collectLayoutIssues(
  layout: LooseLayout,
  registry: ComponentRegistry,
  datasets: ReportDataset[],
  defaultDatasetKey: string,
): Promise<Map<string, string>> {
  // Independent per component, so the Custom chart validations run together.
  const found = await Promise.all(
    layout.items.map(async ({ component }) => {
      const id = String(component.id);
      const type = typeof component.type === 'string' ? component.type : '';

      const def = registry[type];
      if (!def) return [id, `unknown component type "${type}"`] as const;

      const datasetKey =
        typeof component.datasetKey === 'string'
          ? component.datasetKey
          : defaultDatasetKey;
      const dataset = datasets.find((d) => d.key === datasetKey) ?? null;

      const issue =
        columnRefIssue(type, def.columnRefFields, component, dataset) ??
        (type === 'vega-chart'
          ? await customChartIssue(component, dataset)
          : null);
      return [id, issue] as const;
    }),
  );

  return new Map(
    found.filter((entry): entry is readonly [string, string] => !!entry[1]),
  );
}

// ── Orchestration ─────────────────────────────────────────────────────────────

export interface CanvasGenerationResult {
  layout: GridLayout;
  /** Components dropped after the repair round (0 = clean generation). */
  droppedCount: number;
}

type GenerateAttempt = (previousAttempt?: {
  layout: LooseRecord;
  errors: string;
}) => Promise<LooseRecord>;

interface EvaluatedAttempt {
  layout: LooseLayout | null;
  issues: Map<string, string>;
  /** Feedback for the repair round; null = attempt is fully usable. */
  errors: string | null;
}

async function evaluateAttempt(
  raw: LooseRecord,
  registry: ComponentRegistry,
  datasets: ReportDataset[],
  defaultDatasetKey: string,
  datasetKeys: ReadonlySet<string>,
): Promise<EvaluatedAttempt> {
  const normalized = normalizeGeneratedLayout(raw, datasetKeys, registry);
  if (!normalized) {
    return {
      layout: null,
      issues: new Map(),
      errors:
        'the response must be { "items": [ { "x", "y", "w", "h", "component" }, ... ] } with at least one component',
    };
  }

  const parsed = ReportCanvasGridLayoutSchema.safeParse(normalized);
  if (!parsed.success) {
    return {
      layout: null,
      issues: new Map(),
      errors: parsed.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; '),
    };
  }

  const issues = await collectLayoutIssues(
    normalized,
    registry,
    datasets,
    defaultDatasetKey,
  );
  return {
    layout: normalized,
    issues,
    errors: issues.size > 0 ? [...issues.values()].join('; ') : null,
  };
}

/**
 * Runs the full generate → evaluate → repair-once → drop-broken pipeline and
 * returns a hydrated GridLayout ready to become the edit draft. Throws when
 * nothing usable survives; the caller owns user-facing error copy.
 */
export async function runCanvasGeneration(options: {
  generate: GenerateAttempt;
  registry: ComponentRegistry;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
}): Promise<CanvasGenerationResult> {
  const { generate, registry, datasets, defaultDatasetKey } = options;
  const datasetKeys = new Set(datasets.map((d) => d.key));

  let raw = await generate();
  let evaluated = await evaluateAttempt(
    raw,
    registry,
    datasets,
    defaultDatasetKey,
    datasetKeys,
  );

  if (evaluated.errors !== null) {
    raw = await generate({ layout: raw, errors: evaluated.errors });
    evaluated = await evaluateAttempt(
      raw,
      registry,
      datasets,
      defaultDatasetKey,
      datasetKeys,
    );
  }

  if (!evaluated.layout) {
    throw new Error('Generated layout was not usable after one repair round');
  }

  // Post-repair leniency: drop what's still broken instead of failing a
  // mostly-good layout, and tell the caller how much was lost.
  const droppedCount = evaluated.issues.size;
  const kept = evaluated.layout.items.filter(
    ({ component }) => !evaluated.issues.has(String(component.id)),
  );

  if (kept.length === 0) {
    throw new Error('No usable components survived generation');
  }

  // Normalization + schema parse guarantee each kept component has string
  // id/type plus passthrough config — exactly ReportComponentConfig's shape.
  const serialized = ReportCanvasGridLayoutSchema.parse({ items: kept });
  const layout = hydrateLayout(
    deserializeLayout(serialized, registry),
    registry,
  );

  return { layout, droppedCount };
}

// ── One component ─────────────────────────────────────────────────────────────

/**
 * What the model is asked when one component is generated: the same layout
 * contract, scoped to a one-item layout holding that component. Machine-facing.
 */
export function componentGenerationPrompt(
  component: ReportComponentConfig,
  prompt: string,
): string {
  return `Change only the one component in the current layout (id "${component.id}"), as the request asks: ${prompt}\nRespond with a layout of exactly one item holding exactly one component that keeps this id. It may change type when the request asks for a different kind of chart.`;
}

/**
 * Generates or edits one component through the whole-canvas contract: the
 * current layout is just that component, and the first component of the
 * validated result replaces it, keeping its id (its place on the grid is the
 * host's, and stays).
 */
export async function runComponentGeneration(options: {
  component: ReportComponentConfig;
  generate: GenerateAttempt;
  registry: ComponentRegistry;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
}): Promise<ReportComponentConfig> {
  const { component, ...rest } = options;
  const { layout } = await runCanvasGeneration(rest);
  const generated = layout[0]?.component;
  if (!generated) throw new Error('No component survived generation');
  return { ...generated, id: component.id };
}
